import { colors } from '../utils/colors';
import { clerkCssVar } from '../utils/cssVariables';
import type { InternalTheme } from './types';

const textVariants = (t: InternalTheme) => {
  const base = {
    fontFamily: 'inherit',
    letterSpacing: t.letterSpacings.$normal,
  };

  const h1 = {
    ...base,
    fontWeight: t.fontWeights.$semibold,
    fontSize: t.fontSizes.$xl,
    lineHeight: t.lineHeights.$extraSmall,
  } as const;

  const h2 = {
    ...base,
    fontWeight: t.fontWeights.$bold,
    fontSize: t.fontSizes.$lg,
    lineHeight: t.lineHeights.$medium,
  } as const;

  const h3 = {
    ...base,
    fontWeight: t.fontWeights.$bold,
    fontSize: t.fontSizes.$md,
    lineHeight: t.lineHeights.$small,
  } as const;

  const subtitle = {
    ...base,
    fontWeight: t.fontWeights.$medium,
    fontSize: t.fontSizes.$md,
    lineHeight: t.lineHeights.$small,
  } as const;

  const body = {
    ...base,
    fontWeight: t.fontWeights.$normal,
    fontSize: t.fontSizes.$md,
    lineHeight: t.lineHeights.$small,
  } as const;

  const caption = {
    ...base,
    fontWeight: t.fontWeights.$medium,
    fontSize: t.fontSizes.$xs,
    lineHeight: t.lineHeights.$large,
  } as const;

  const buttonLarge = {
    ...base,
    fontWeight: t.fontWeights.$medium,
    fontSize: t.fontSizes.$md,
    lineHeight: t.lineHeights.$small,
    fontFamily: t.fonts.$buttons,
  } as const;

  const buttonSmall = {
    ...base,
    fontWeight: t.fontWeights.$medium,
    fontSize: t.fontSizes.$sm,
    lineHeight: t.lineHeights.$extraSmall,
    fontFamily: t.fonts.$buttons,
  } as const;

  return {
    h1,
    h2,
    h3,
    subtitle,
    body,
    caption,
    buttonLarge,
    buttonSmall,
  } as const;
};

const borderVariants = (t: InternalTheme, props?: any) => {
  const hoverBorderColor = !props?.hasError ? t.colors.$borderAlpha300 : t.colors.$dangerAlpha500;
  const hoverBoxShadow = t.shadows.$input.replace(
    '{{color}}',
    !props?.hasError ? t.colors.$borderAlpha150 : t.colors.$dangerAlpha200,
  );
  const hoverStyles =
    props?.hoverStyles === false
      ? {}
      : {
          '&:hover': {
            WebkitTapHighlightColor: 'transparent',
            borderColor: hoverBorderColor,
            boxShadow: hoverBoxShadow,
          },
        };
  const borderColor = !props?.hasError
    ? !props?.hasWarning
      ? t.colors.$borderAlpha150
      : t.colors.$warningAlpha300
    : t.colors.$dangerAlpha500;

  const boxShadow = !props?.hasError
    ? !props?.hasWarning
      ? t.colors.$borderAlpha100
      : t.colors.$warningAlpha50
    : t.colors.$borderAlpha150;

  const defaultBoxShadow = t.shadows.$input.replace('{{color}}', boxShadow);

  const focusStyleValues = {
    borderColor: hoverBorderColor,
    WebkitTapHighlightColor: 'transparent',
    boxShadow: [
      hoverBoxShadow,
      t.shadows.$focusRing.replace(
        '{{color}}',
        !props?.hasError ? t.colors.$borderAlpha150 : (t.colors.$dangerAlpha200 as string),
      ),
    ].toString(),
  };
  const focusStyles =
    props?.focusRing === false
      ? {}
      : {
          '&:focus': focusStyleValues,
          '&:focus:not(:focus-visible)': {
            borderColor,
            boxShadow: defaultBoxShadow,
          },
          '&:focus-visible': focusStyleValues,
        };

  return {
    normal: {
      borderRadius: t.radii.$md,
      borderWidth: t.borderWidths.$normal,
      borderStyle: t.borderStyles.$solid,
      borderColor,
      boxShadow: defaultBoxShadow,
      transitionProperty: t.transitionProperty.$common,
      transitionTimingFunction: t.transitionTiming.$common,
      transitionDuration: t.transitionDuration.$focusRing,
      ...hoverStyles,
      ...focusStyles,
    },
  } as const;
};

const borderColor = (t: InternalTheme, props?: any) => {
  return {
    borderColor: props?.hasError ? t.colors.$dangerAlpha500 : t.colors.$borderAlpha150,
  } as const;
};

const focusRingStyles = (t: InternalTheme) => {
  return {
    '&::-moz-focus-inner': { border: '0' },
    WebkitTapHighlightColor: 'transparent',
    boxShadow: t.shadows.$focusRing.replace('{{color}}', t.colors.$colorRing),
    transitionProperty: t.transitionProperty.$common,
    transitionTimingFunction: t.transitionTiming.$common,
    transitionDuration: t.transitionDuration.$focusRing,
  } as const;
};

const focusRing = (t: InternalTheme) => {
  return {
    '&:focus': {
      ...focusRingStyles(t),
    },
    '&:focus:not(:focus-visible)': {
      boxShadow: 'none',
    },
    '&:focus-visible': {
      ...focusRingStyles(t),
    },
  } as const;
};

const disabled = (t: InternalTheme) => {
  return {
    '&:disabled,&[data-disabled]': {
      cursor: 'not-allowed',
      pointerEvents: 'none',
      opacity: t.opacity.$disabled,
    },
  } as const;
};

// Shared chrome for a button anchored to the trailing edge of an input (e.g. password reveal,
// search clear). The clerk theme tightens the geometry in `baseTheme.ts`; the simple theme uses
// these values as-is. Callers layer on their own icon/visibility styles.
const inputTrailingButton = (t: InternalTheme) =>
  ({
    position: 'absolute',
    insetInlineEnd: t.space.$1,
    insetBlock: t.space.$1,
    borderRadius: `calc(${t.radii.$md} - ${t.space.$1})`,
    color: t.colors.$neutralAlpha400,
    paddingInline: t.space.$2,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: `calc(${t.space.$1} * -1)`,
      display: 'block',
      borderRadius: t.radii.$md,
    },
  }) as const;

const centeredFlex = (display: 'flex' | 'inline-flex' = 'flex') => ({
  display: display,
  justifyContent: 'center',
  alignItems: 'center',
});

const unstyledScrollbar = (t: InternalTheme) => {
  // Chrome 121+ prioritizes standardized properties over webkit when both are present
  // So we only apply standardized properties for Firefox (which doesn't support webkit)
  // Chrome/Safari/Edge will use the webkit properties below
  const baseStyles: any = {
    // Webkit (Chrome, Safari, Edge) - full styling support
    '::-webkit-scrollbar': {
      background: t.colors.$neutralAlpha50,
      width: '6px',
      height: '6px',
    },
    '::-webkit-scrollbar-thumb': {
      background: t.colors.$neutralAlpha600,
      borderRadius: t.radii.$sm,
      transition: `background-color ${t.transitionDuration.$fast} ${t.transitionTiming.$common}`,
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: t.colors.$neutralAlpha700,
    },
    '::-webkit-scrollbar-track': {
      background: t.colors.$neutralAlpha50,
    },
  };

  // Firefox support - wrapped in @supports so Chrome doesn't use it
  // Emotion supports @supports queries in sx objects
  baseStyles['@supports not selector(::-webkit-scrollbar)'] = {
    scrollbarColor: `${t.colors.$neutralAlpha600} ${t.colors.$neutralAlpha50}`,
    scrollbarWidth: 'thin',
  };

  return baseStyles;
};

const maxHeightScroller = (t: InternalTheme) =>
  ({
    height: '100%',
    overflowY: 'auto',
    ...unstyledScrollbar(t),
  }) as const;

const mergedColorsBackground = (colorBack: string, colorFront: string) => {
  return `linear-gradient(${colorFront},${colorFront}), linear-gradient(${colorBack}, ${colorBack})`;
};

const mutedBackground = (t: InternalTheme) => {
  const defaultColor =
    t.colors.$colorMuted ||
    mergedColorsBackground(colors.setAlpha(t.colors.$colorBackground, 1), t.colors.$neutralAlpha50);
  return clerkCssVar('color-muted', defaultColor);
};

const visuallyHidden = () =>
  ({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: '1px',
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  }) as const;

export const common = {
  textVariants,
  borderVariants,
  focusRingStyles,
  focusRing,
  disabled,
  borderColor,
  inputTrailingButton,
  centeredFlex,
  maxHeightScroller,
  mutedBackground,
  unstyledScrollbar,
  mergedColorsBackground,
  visuallyHidden,
};
